import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { api, unwrap } from "@/shared/lib/apiClient";
import { setupKeys } from "@/shared/lib/queryKeys";
import {
  addCreatedStandInOptimistic,
  copyPreviousBossOptimistic,
  createBossViewOptimistic,
  deleteBossViewOptimistic,
  removeUnavailableAssignments,
  setBossAssignmentOptimistic,
  setBossPlayersOptimistic,
  setBossSkippedOptimistic,
  setPlayerBossesOptimistic,
} from "../setup.bosses";
import { captureElementPng } from "../setup.capture";
import { applyOverride, applyStandInUpdate } from "../setup.overrides";
import type { StandInFormValues } from "../setup.schemas";
import type {
  OverrideBody,
  PlayerKind,
  SetupPlayer,
  SetupResponse,
  StandInUpdate,
} from "../setup.types";

// Shared optimistic plumbing: cancel the poll, snapshot, patch, rollback on error, refetch on settle.
function useOptimisticSetupMutation<TVars>(
  raidId: string,
  errorTitle: string,
  mutationFn: (vars: TVars) => Promise<unknown>,
  patch: (data: SetupResponse, vars: TVars) => SetupResponse,
) {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn,
    onMutate: async (vars: TVars) => {
      await queryClient.cancelQueries({ queryKey: setupKeys.byRaid(raidId) });
      const previous = queryClient.getQueryData<SetupResponse>(
        setupKeys.byRaid(raidId),
      );
      if (previous) {
        queryClient.setQueryData(
          setupKeys.byRaid(raidId),
          patch(previous, vars),
        );
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(setupKeys.byRaid(raidId), ctx.previous);
      }
      showApiError(err, { title: errorTitle });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: setupKeys.byRaid(raidId) }),
  });
}

function patchPlayer(
  data: SetupResponse,
  id: string,
  kind: PlayerKind,
  update: (p: SetupPlayer) => SetupPlayer,
): SetupResponse {
  return {
    ...data,
    players: data.players.map((p) =>
      p.id === id && p.kind === kind ? update(p) : p,
    ),
  };
}

export function useUpsertOverride(raidId: string) {
  return useOptimisticSetupMutation<OverrideBody & { userId: string }>(
    raidId,
    "Kunde inte uppdatera spelaren",
    async ({ userId, ...body }) =>
      unwrap(
        await api.PUT("/api/setup/{raidId}/members/{userId}", {
          params: { path: { raidId, userId } },
          body,
        }),
      ),
    (data, { userId, ...body }) => {
      const patched = patchPlayer(data, userId, "member", (p) =>
        applyOverride(p, body),
      );
      return body.benched ? removeUnavailableAssignments(patched) : patched;
    },
  );
}

export function useCreateStandIn(raidId: string) {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  // Insert after the server supplies the id, including its initial all-boss selection.
  return useMutation({
    mutationFn: async (body: StandInFormValues) =>
      unwrap(
        await api.POST("/api/setup/{raidId}/stand-ins", {
          params: { path: { raidId } },
          body,
        }),
      ),
    onSuccess: (created) => {
      queryClient.setQueryData<SetupResponse>(
        setupKeys.byRaid(raidId),
        (data) =>
          data ? addCreatedStandInOptimistic(data, created) : undefined,
      );
      queryClient.invalidateQueries({ queryKey: setupKeys.byRaid(raidId) });
    },
    onError: (err) => showApiError(err, { title: "Kunde inte spara stand-in" }),
  });
}

export function useUpdateStandIn(raidId: string) {
  return useOptimisticSetupMutation<StandInUpdate>(
    raidId,
    "Kunde inte spara stand-in",
    async ({ id, ...body }) =>
      unwrap(
        await api.PUT("/api/setup/stand-ins/{id}", {
          params: { path: { id } },
          body,
        }),
      ),
    (data, vars) => {
      const patched = patchPlayer(data, vars.id, "stand_in", (p) =>
        applyStandInUpdate(p, vars),
      );
      return vars.benched ? removeUnavailableAssignments(patched) : patched;
    },
  );
}

export function useDeleteStandIn(raidId: string) {
  return useOptimisticSetupMutation<{ id: string }>(
    raidId,
    "Kunde inte ta bort stand-in",
    async ({ id }) => {
      unwrap(
        await api.DELETE("/api/setup/stand-ins/{id}", {
          params: { path: { id } },
        }),
      );
    },
    (data, { id }) =>
      removeUnavailableAssignments({
        ...data,
        players: data.players.filter(
          (p) => !(p.kind === "stand_in" && p.id === id),
        ),
      }),
  );
}

export function useCreateBossView(raidId: string) {
  return useOptimisticSetupMutation<void>(
    raidId,
    "Kunde inte skapa setup per boss",
    async () => {
      unwrap(
        await api.POST("/api/setup/{raidId}/boss-view", {
          params: { path: { raidId } },
        }),
      );
    },
    createBossViewOptimistic,
  );
}

export function useDeleteBossView(raidId: string) {
  return useOptimisticSetupMutation<void>(
    raidId,
    "Kunde inte ta bort setup per boss",
    async () => {
      unwrap(
        await api.DELETE("/api/setup/{raidId}/boss-view", {
          params: { path: { raidId } },
        }),
      );
    },
    deleteBossViewOptimistic,
  );
}

type SetBossAssignmentVariables = {
  bossId: string;
  participantKind: PlayerKind;
  participantId: string;
  selected: boolean;
};

export function useToggleBossAssignment(raidId: string) {
  return useOptimisticSetupMutation<SetBossAssignmentVariables>(
    raidId,
    "Kunde inte uppdatera bossvalet",
    async ({ bossId, participantKind, participantId, selected }) => {
      unwrap(
        await api.PUT(
          "/api/setup/{raidId}/boss-view/bosses/{bossId}/assignment",
          {
            params: { path: { raidId, bossId } },
            body: { participantKind, participantId, selected },
          },
        ),
      );
    },
    (data, { bossId, participantKind, participantId, selected }) =>
      setBossAssignmentOptimistic(
        data,
        bossId,
        { id: participantId, kind: participantKind },
        selected,
      ),
  );
}

type SetPlayerBossesVariables = {
  participantKind: PlayerKind;
  participantId: string;
  selected: boolean;
};

export function useSetPlayerBosses(raidId: string) {
  return useOptimisticSetupMutation<SetPlayerBossesVariables>(
    raidId,
    "Kunde inte uppdatera spelarens bossval",
    async ({ participantKind, participantId, selected }) => {
      unwrap(
        await api.PUT(
          "/api/setup/{raidId}/boss-view/players/{participantKind}/{participantId}",
          {
            params: {
              path: { raidId, participantKind, participantId },
            },
            body: { selected },
          },
        ),
      );
    },
    (data, { participantKind, participantId, selected }) =>
      setPlayerBossesOptimistic(
        data,
        { id: participantId, kind: participantKind },
        selected,
      ),
  );
}

type SetBossPlayersVariables = {
  bossId: string;
  selected: boolean;
};

export function useSetBossPlayers(raidId: string) {
  return useOptimisticSetupMutation<SetBossPlayersVariables>(
    raidId,
    "Kunde inte uppdatera bossens spelare",
    async ({ bossId, selected }) => {
      unwrap(
        await api.PUT("/api/setup/{raidId}/boss-view/bosses/{bossId}", {
          params: { path: { raidId, bossId } },
          body: { selected },
        }),
      );
    },
    (data, { bossId, selected }) =>
      setBossPlayersOptimistic(data, bossId, selected),
  );
}

export function useCopyPreviousBoss(raidId: string) {
  return useOptimisticSetupMutation<{ bossId: string }>(
    raidId,
    "Kunde inte kopiera föregående boss",
    async ({ bossId }) => {
      unwrap(
        await api.POST(
          "/api/setup/{raidId}/boss-view/bosses/{bossId}/copy-previous",
          { params: { path: { raidId, bossId } } },
        ),
      );
    },
    (data, { bossId }) => copyPreviousBossOptimistic(data, bossId),
  );
}

export function useSetBossSkipped(raidId: string) {
  return useOptimisticSetupMutation<{ bossId: string; skipped: boolean }>(
    raidId,
    "Kunde inte uppdatera bossen",
    async ({ bossId, skipped }) => {
      unwrap(
        await api.PUT("/api/setup/{raidId}/boss-view/bosses/{bossId}/skip", {
          params: { path: { raidId, bossId } },
          body: { skipped },
        }),
      );
    },
    (data, { bossId, skipped }) =>
      setBossSkippedOptimistic(data, bossId, skipped),
  );
}

// Publishes a screenshot of the boss matrix to Discord. Not optimistic and not
// cache-touching: it captures the given `<table>` node to a PNG and uploads it as
// multipart/form-data. Errors (capture or upload) surface in the shared dialog.
export function usePublishBossSetup(raidId: string) {
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async (node: HTMLElement) => {
      const png = await captureElementPng(node);
      const form = new FormData();
      form.append("image", png, "boss-setup.png");
      unwrap(
        await api.POST("/api/setup/{raidId}/publish", {
          params: { path: { raidId } },
          // Multipart upload: FormData carries the PNG; the passthrough serializer
          // lets openapi-fetch send it as-is (and drop the JSON content-type so the
          // browser sets the multipart boundary).
          body: form as unknown as { image: string },
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onError: (err) =>
      showApiError(err, { title: "Kunde inte publicera setupen" }),
  });
}
